import i18n from '@/i18n';

/** Normalized content shape for filtering (does not replace Post.type). */
export type UIContentType = 'video' | 'story' | 'multi_image' | 'photo';

export type PlatformFilterKey = 'all' | 'Instagram' | 'TikTok' | 'YouTube';

export interface ContentFilterOption {
  key: string;
  label: string;
  /** When set, selected filter keeps posts whose UI type is in this list. */
  types?: UIContentType[];
}

/** Minimal post fields used for content filtering (avoids circular imports). */
export interface PostContentFilterFields {
  type: 'Reel' | 'Story' | 'Carousel';
  product_type?: string;
  platform?: 'Instagram' | 'TikTok' | 'YouTube';
}

/** Buckets for "All platforms" content filters (driven by product_type, fallback to Post.type + platform). */
type AllPlatformsCategory = 'reel' | 'story' | 'carousel' | 'videos' | 'photoPosts';

function allPlatformsCategoryFromProductType(pt: string | undefined): AllPlatformsCategory | null {
  const u = (pt || '').toUpperCase();
  if (!u) return null;
  if (u.includes('STORY')) return 'story';
  if (u.includes('TIKTOK') && u.includes('CAROUSEL')) return 'photoPosts';
  if (u.includes('CAROUSEL') || u.includes('CARROUSEL')) return 'carousel';
  if (u.includes('PICTURE')) return 'photoPosts';
  if (u.includes('REEL')) return 'reel';
  if (u.includes('VIDEO')) return 'videos';
  if (u.includes('INSTAGRAM') && (u.includes('POST') || u.includes('FEED'))) return 'reel';
  return null;
}

function allPlatformsCategoryFallback(post: PostContentFilterFields): AllPlatformsCategory {
  if (post.type === 'Story') return 'story';
  if (post.type === 'Carousel') return 'carousel';
  const plat = post.platform;
  if (plat === 'TikTok' || plat === 'YouTube') return 'videos';
  return 'reel';
}

/**
 * When platform filter is "all", matches Reel / Story / Carousel / Videos / Photo posts using
 * product_type first, then legacy Post.type + post.platform. Does not replace Post.type elsewhere.
 */
export function postMatchesAllPlatformsContentFilter(
  post: PostContentFilterFields,
  filterKey: string,
): boolean {
  const cat =
    allPlatformsCategoryFromProductType(post.product_type) ?? allPlatformsCategoryFallback(post);
  switch (filterKey) {
    case 'Reel':
      return cat === 'reel';
    case 'Story':
      return cat === 'story';
    case 'Carousel':
      return cat === 'carousel';
    case 'videos':
      return cat === 'videos';
    case 'photoPosts':
      return cat === 'photoPosts';
    default:
      return false;
  }
}

/**
 * Maps API `product_type` strings to a coarse UI bucket for platform-aware filters.
 * Does not change or replace `Post.type` inference elsewhere.
 */
export function getUIContentType(product_type: string | undefined): UIContentType {
  const value = (product_type || '').toUpperCase();
  if (value.includes('STORY')) return 'story';
  if (value.includes('CAROUSEL') || value.includes('CARROUSEL')) return 'multi_image';
  if (value.includes('PICTURE')) return 'photo';
  if (value.includes('VIDEO')) return 'video';
  return 'video';
}

/** When `product_type` is missing, derive a UI bucket from legacy `Post.type`. */
export function postTypeToFallbackUIContentType(type: 'Reel' | 'Story' | 'Carousel'): UIContentType {
  if (type === 'Story') return 'story';
  if (type === 'Carousel') return 'multi_image';
  return 'video';
}

/**
 * Short label on the publication card chip (thumbnail corner). Platform-aware: e.g. Instagram short video →
 * "Reel", TikTok / YouTube video → "Video". Does not change `Post.type` used elsewhere.
 */
export function publicationPostTypeChipLabel(
  post: PostContentFilterFields,
  fallbackPlatform?: PostContentFilterFields['platform'],
): string {
  const pt = (post.product_type || '').toUpperCase();
  const plat = post.platform ?? fallbackPlatform;

  if (post.type === 'Story' || pt.includes('STORY')) {
    return i18n.t('contentReview.postChip.story');
  }
  if (post.type === 'Carousel' || pt.includes('CAROUSEL') || pt.includes('CARROUSEL')) {
    return i18n.t('contentReview.postChip.carousel');
  }
  if (pt.includes('PICTURE') || (pt.includes('PHOTO') && !pt.includes('VIDEO'))) {
    return i18n.t('contentReview.postChip.photo');
  }

  if (plat === 'Instagram') {
    return i18n.t('contentReview.postChip.reel');
  }
  if (plat === 'TikTok' || plat === 'YouTube') {
    return i18n.t('contentReview.postChip.video');
  }

  if (pt.includes('INSTAGRAM')) {
    return i18n.t('contentReview.postChip.reel');
  }
  if (pt.includes('TIKTOK') || pt.includes('YOUTUBE')) {
    return i18n.t('contentReview.postChip.video');
  }

  return i18n.t('contentReview.postChip.video');
}

export function getPlatformContentFilterOptions(platformFilter: PlatformFilterKey): ContentFilterOption[] {
  if (platformFilter === 'all') {
    return [
      { key: 'all', label: i18n.t('contentReview.filters.allPosts') },
      { key: 'Reel', label: i18n.t('contentReview.filters.reels') },
      { key: 'Story', label: i18n.t('contentReview.filters.stories') },
      { key: 'Carousel', label: i18n.t('contentReview.filters.carousel') },
      { key: 'videos', label: i18n.t('contentReview.filters.videos') },
      { key: 'photoPosts', label: i18n.t('contentReview.filters.photoPosts') },
    ];
  }
  if (platformFilter === 'Instagram') {
    return [
      { key: 'all', label: i18n.t('contentReview.filters.allPosts') },
      { key: 'reels', label: i18n.t('contentReview.filters.reels'), types: ['video'] },
      { key: 'stories', label: i18n.t('contentReview.filters.stories'), types: ['story'] },
      { key: 'carousel', label: i18n.t('contentReview.filters.carousel'), types: ['multi_image'] },
    ];
  }
  if (platformFilter === 'TikTok') {
    return [
      { key: 'all', label: i18n.t('contentReview.filters.allPosts') },
      { key: 'videos', label: i18n.t('contentReview.filters.videos'), types: ['video'] },
      { key: 'photos', label: i18n.t('contentReview.filters.photoPosts'), types: ['photo', 'multi_image'] },
    ];
  }
  if (platformFilter === 'YouTube') {
    return [
      { key: 'all', label: i18n.t('contentReview.filters.allPosts') },
      { key: 'videos', label: i18n.t('contentReview.filters.videos'), types: ['video'] },
    ];
  }
  return [{ key: 'all', label: i18n.t('contentReview.filters.allPosts') }];
}

/** "All posts" first, then remaining options sorted alphabetically by visible label (locale-aware). */
export function sortContentFilterOptionsAlphabetically(options: ContentFilterOption[]): ContentFilterOption[] {
  const allOpt = options.find((o) => o.key === 'all');
  const rest = options.filter((o) => o.key !== 'all');
  rest.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  return allOpt ? [allOpt, ...rest] : rest;
}

/**
 * Non-breaking: when `platformFilter === 'all'`, matches legacy `Post.type` only.
 * When a single platform is selected, uses `product_type` via `getUIContentType`, with fallback to `Post.type`.
 */
export function postMatchesContentFilter(
  post: PostContentFilterFields,
  platformFilter: PlatformFilterKey,
  filterKey: string,
): boolean {
  if (filterKey === 'all') return true;

  if (platformFilter === 'all') {
    return postMatchesAllPlatformsContentFilter(post, filterKey);
  }

  const options = getPlatformContentFilterOptions(platformFilter);
  const opt = options.find((o) => o.key === filterKey);
  if (!opt?.types?.length) return true;

  const ui =
    post.product_type && post.product_type.trim().length > 0
      ? getUIContentType(post.product_type)
      : postTypeToFallbackUIContentType(post.type);

  return opt.types.includes(ui);
}
